import { fastSplice, range } from "./arrays";
import { mapGetOrElse } from "./maps";

declare const global: any;

// polyfill RAF
if (typeof requestAnimationFrame !== "function") {
    global.requestAnimationFrame = global.setImmediate.bind(global);
}

const callbacks: { callback: Function; timer: number }[] = [];
const rafActiveFlags = new Map<string, boolean>();

export function Throttle<U>(
    fns: Array<() => Promise<U>>,
    maxRenders: number = 4,
): Promise<Array<U>> {
    let activeProms: Array<number> = [];
    let queued = range(0, fns.length).map((i) => i);
    let completedResults: Array<U> = [];
    let resolve: (value: PromiseLike<Array<U>> | Array<U>) => void;

    // Check for zero length list
    if (fns.length === 0) return Promise.resolve(undefined as any);

    for (let i = 0; i < Math.min(fns.length, maxRenders); i++) {
        run();
    }

    return new Promise((r) => {
        resolve = r;
    });

    function run() {
        if (queued.length === 0) return;

        if (activeProms.length > maxRenders) {
            console.warn(
                `Too many concurrent promises. (${activeProms.length}) active`,
                activeProms.slice(),
            );
            return;
        } else if (activeProms.length === maxRenders) {
            return;
        } else if (queued.length === 0) {
            console.warn(
                `Length of queued was 0. queued and activeProms have desynchronized. activeProms length=${activeProms.length}`,
            );
            return;
        }

        let runIndex = queued.shift() as number; // already checked its length earlier
        console.debug(`Running ${runIndex + 1} / ${fns.length}`);

        let prom = fns[runIndex]();
        activeProms.push(runIndex);

        prom.then((it) => complete(runIndex, it));

        // Keep trying to run more. preconditions at top will early exit when needed
        run();
    }

    function complete(runIndex: number, result: U) {
        let i = activeProms.indexOf(runIndex);
        if (i === -1) {
            console.warn(
                "Tried to complete a promise that isn't running ",
                runIndex,
                activeProms.slice(),
            );
            return;
        }
        activeProms.splice(i, 1);
        completedResults[runIndex] = result;

        // Try to batch up the starts of multiple runs by starting at the next frame update
        rafOnce("throttle", () => run());

        if (completedResults.length === fns.length) {
            resolve(completedResults);
        }
    }
}

export type Deferrable<T, U> = {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (value?: U) => void;
};

export function DeferredPromise<T, U = never>(): Deferrable<T, U> {
    let resolve: (value: T) => void = undefined as any,
        reject: (value?: U) => void = undefined as any;
    let promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });

    return { promise, resolve, reject };
}

/** Returns a function that immediately resolves the callback */
export function debounce(
    time: number,
    callback: () => any,
): { resolve: () => void; cancel: () => void } {
    let lookupIndex = callbacks.findIndex((it) => it.callback === callback);
    let active: typeof callbacks[0] =
        lookupIndex !== -1 ? callbacks[lookupIndex] : (undefined as any);

    if (lookupIndex === -1) {
        const timer = window.setTimeout(() => {
            let index = callbacks.findIndex((it) => it.callback === callback);
            if (index !== -1) {
                resolve();
            } else {
                console.warn("Unable to find function in callbacks", callback, time);
            }
        }, time);
        active = { callback, timer };
        callbacks.push(active);
        return { resolve: () => resolve(), cancel: () => cancel() };
    } else {
        return { resolve: () => resolve(), cancel: () => cancel() };
    }

    function cancel() {
        window.clearTimeout(active.timer);
        let index = callbacks.findIndex((it) => it.callback === callback);
        if (index !== -1) fastSplice(callbacks, index, 1);
    }

    function resolve() {
        window.clearTimeout(active.timer);
        callback();
        let index = callbacks.findIndex((it) => it.callback === callback);
        if (index !== -1) fastSplice(callbacks, index, 1);
    }
}

export function rafOnce<T>(id: string, cb: (elapsed: number) => void | T) {
    let flag = mapGetOrElse(rafActiveFlags, id, false);
    if (!flag) {
        rafActiveFlags.set(id, true);
        requestAnimationFrame((elapsed) => {
            rafActiveFlags.set(id, false);
            cb(elapsed);
        });
    }
}
